import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { formatDateTime, formatCurrency } from './utils';
import { Clock, MapPin, Settings } from 'lucide-react';

interface ParkingSessionService {
	serviceId: string;
	serviceCode: string;
	serviceName: string;
	serviceDescription: string;
	priceCharged: number;
	freeParkingMinutes: number;
	serviceTime: Date;
	facilityName: string;
}

interface ParkingSessionServicesProps {
	sessionId: string;
}

// Dummy data generator based on Operations.ParkingSessionService specification
const generateDummyServiceData = (seed = 0): ParkingSessionService[] => {
	const services = [
		{
			code: 'CAR_WASH_BASIC',
			name: 'Basic Car Wash',
			description: 'Exterior car wash with soap and rinse',
			basePrice: 15,
			freeParkingMinutes: 30
		},
		{
			code: 'CAR_WASH_PREMIUM',
			name: 'Premium Car Wash',
			description: 'Full interior and exterior cleaning service',
			basePrice: 35,
			freeParkingMinutes: 60
		},
		{
			code: 'TIRE_CHECK',
			name: 'Tire Pressure Check',
			description: 'Professional tire pressure and condition inspection',
			basePrice: 8,
			freeParkingMinutes: 15
		},
		{
			code: 'BATTERY_CHECK',
			name: 'Battery Test',
			description: 'Vehicle battery performance test and report',
			basePrice: 12,
			freeParkingMinutes: 20
		},
		{
			code: 'VALET_PARKING',
			name: 'Valet Parking Service',
			description: 'Professional valet parking and vehicle care',
			basePrice: 25,
			freeParkingMinutes: 0
		}
	];

	// Generate 2-3 random services for this session using seed for consistency
	const shuffledServices = [...services];
	// Use seed for deterministic "random" selection
	const serviceCount = (seed % 3) + 1; // 1-3 services
	const selectedServices = shuffledServices.slice(0, serviceCount);

	return selectedServices.map((service, index) => ({
		serviceId: `srv_${String(index + 1).padStart(3, '0')}`,
		serviceCode: service.code,
		serviceName: service.name,
		serviceDescription: service.description,
		priceCharged: service.basePrice * (0.8 + Math.random() * 0.4), // Random price variation
		freeParkingMinutes: service.freeParkingMinutes,
		serviceTime: new Date(Date.now() - Math.random() * 3600000 * 2), // Random time within last 2 hours
		facilityName: 'Downtown Parking Center'
	}));
};

export const ParkingSessionServices = ({ sessionId }: ParkingSessionServicesProps) => {
	const { t } = useTranslation('parkingSessions');
	
	// Generate dummy data based on sessionId for consistency
	// Use sessionId to seed random generation for consistent results
	const sessionSeed = sessionId.split('').reduce((acc, char) => acc + (char.codePointAt(0) || 0), 0);
	const services = generateDummyServiceData(sessionSeed);

	if (services.length === 0) {
		return (
			<div className="space-y-6">
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<Settings className="w-5 h-5" />
							{t('services.title')}
						</CardTitle>
						<CardDescription>
							{t('services.servicesNote')}
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="py-12 text-center text-muted-foreground">
							<Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
							<p>{t('services.noServices')}</p>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	const totalAmount = services.reduce((sum, service) => sum + service.priceCharged, 0);
	const totalFreeMinutes = services.reduce((sum, service) => sum + service.freeParkingMinutes, 0);

	return (
		<div className="space-y-6">
			{/* Header */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Settings className="w-5 h-5" />
						{t('services.title')}
					</CardTitle>
					<CardDescription>
						{t('services.servicesNote')}
					</CardDescription>
				</CardHeader>
			</Card>

			{/* Services List */}
			<div className="grid gap-4">
				{services.map((service) => (
					<Card key={service.serviceId} className="border-l-4 border-l-blue-500">
						<CardContent className="p-6">
							<div className="flex items-start justify-between mb-4">
								<div className="flex-1">
									<div className="flex items-center gap-3 mb-2">
										<Badge variant="outline" className="font-mono text-xs">
											{service.serviceCode}
										</Badge>
										<h3 className="text-lg font-semibold">{service.serviceName}</h3>
									</div>
									<p className="mb-3 text-muted-foreground">
										{service.serviceDescription}
									</p>
								</div>
								<div className="ml-4 text-right">
									<div className="text-2xl font-bold text-green-600">
										{formatCurrency(service.priceCharged)}
									</div>
									{service.freeParkingMinutes > 0 && (
										<div className="text-sm font-medium text-blue-600">
											+{service.freeParkingMinutes} {t('services.minutes')}
										</div>
									)}
								</div>
							</div>

							<Separator className="my-4" />

							<div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
								<div className="flex items-center gap-2">
									<Clock className="w-4 h-4 text-muted-foreground" />
									<div>
										<div className="text-muted-foreground">{t('services.serviceTime')}</div>
										<div className="font-medium">
											{formatDateTime(service.serviceTime.toISOString())}
										</div>
									</div>
								</div>
								
								<div className="flex items-center gap-2">
									<MapPin className="w-4 h-4 text-muted-foreground" />
									<div>
										<div className="text-muted-foreground">{t('services.facility')}</div>
										<div className="font-medium">{service.facilityName}</div>
									</div>
								</div>

								{service.freeParkingMinutes > 0 && (
									<div className="flex items-center gap-2">
										<div className="flex items-center justify-center w-4 h-4 bg-blue-100 rounded-full">
											<div className="w-2 h-2 bg-blue-600 rounded-full"></div>
										</div>
										<div>
											<div className="text-muted-foreground">{t('services.freeParkingMinutes')}</div>
											<div className="font-medium text-blue-600">
												{service.freeParkingMinutes} {t('services.minutes')}
											</div>
										</div>
									</div>
								)}
							</div>
						</CardContent>
					</Card>
				))}
			</div>

			{/* Summary */}
			<Card className="border-t-4 border-t-green-500">
				<CardContent className="p-6">
					<div className="grid grid-cols-1 gap-6 md:grid-cols-3">
						<div className="text-center">
							<div className="text-2xl font-bold text-green-600">
								{formatCurrency(totalAmount)}
							</div>
							<div className="text-sm text-muted-foreground">
								{t('services.totalServicesAmount')}
							</div>
						</div>
						
						<div className="text-center">
							<div className="text-2xl font-bold">
								{services.length}
							</div>
							<div className="text-sm text-muted-foreground">
								{t('services.servicesCount')}
							</div>
						</div>

						{totalFreeMinutes > 0 && (
							<div className="text-center">
								<div className="text-2xl font-bold text-blue-600">
									{totalFreeMinutes} {t('services.minutes')}
								</div>
								<div className="text-sm text-muted-foreground">
									{t('services.freeParkingBonus')}
								</div>
							</div>
						)}
					</div>
				</CardContent>
			</Card>
		</div>
	);
};
